import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { CalendarIcon, Check, ChevronLeft, Flame, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { cn } from '../lib/utils';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import Footer from '../components/Footer';
import { siteData } from '../mock';

// Compute next Sundays for a friendly hint (not required)
const hearOptions = [
  'Family / Friend Invite',
  'Social Media | Instagram, Facebook, YouTube',
  'Self Search Via Web/App',
  'Invite Card/Flyer',
  'Used To Attend This Church Before',
];

const FieldCard = ({ children, className = '' }) => (
  <div
    className={cn(
      'bg-[#f5efe9] rounded-2xl px-6 md:px-8 py-5 md:py-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] border border-black/5',
      className
    )}
  >
    {children}
  </div>
);

const PlanVisit = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    sunday: null,
    hearAbout: '',
  });

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.sunday || !form.hearAbout) {
      toast({
        title: 'Almost there!',
        description: 'Please fill in every field so we can prepare for your visit.',
      });
      return;
    }
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/visits`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        sunday: format(form.sunday, 'yyyy-MM-dd'),
        hear_about: form.hearAbout,
      });
      setSubmitted(true);
      toast({
        title: `See you soon, ${form.name.split(' ')[0]}!`,
        description: `We've saved your visit for ${format(form.sunday, 'EEEE, MMMM d, yyyy')}.`,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        'Something went wrong on our end. Please try again in a moment.';
      toast({ title: 'Submission failed', description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#efe6df] flex flex-col">
      {/* Slim brand bar */}
      <header className="bg-[#0a1030] text-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px] md:text-[15px]">
              APOSTOLIC CHRIST TABERNACLE
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Back to home
          </button>
        </div>
      </header>

      <main className="flex-1 py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-[760px] mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h1
              className="text-[42px] md:text-[58px] leading-[1.05] text-[#1a1a1a]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              We Can't Wait to Meet You!
            </h1>
            <p className="mt-4 text-[#3d3d3d] text-[16px] md:text-[17px] max-w-[520px] mx-auto">
              Fill out the form below and our team will be looking out for you the moment you walk in.
            </p>
          </div>

          {submitted ? (
            <FieldCard className="text-center py-14">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#0a1030] text-white flex items-center justify-center mb-5">
                <Check className="h-7 w-7" />
              </div>
              <h2
                className="text-[30px] md:text-[38px] leading-tight text-[#1a1a1a]"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                You're on the list, {form.name.split(' ')[0]}!
              </h2>
              <p className="mt-3 text-[#3d3d3d] max-w-[440px] mx-auto">
                We can't wait to meet you on{' '}
                <span className="font-semibold text-[#1a1a1a]">
                  {format(form.sunday, 'EEEE, MMMM d, yyyy')}
                </span>
                . Someone from our team will reach out shortly.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white px-8 h-12"
                >
                  Back to home
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: '', email: '', phone: '', sunday: null, hearAbout: '' });
                  }}
                  className="rounded-full border-[#0a1030] text-[#0a1030] hover:bg-[#0a1030]/5 px-8 h-12"
                >
                  Submit another
                </Button>
              </div>
            </FieldCard>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <FieldCard>
                <Label htmlFor="name" className="text-[15px] md:text-[16px] font-bold text-[#1a1a1a]">
                  May We Get Your Name?
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="John Doe"
                  className="mt-2 border-0 border-b border-black/10 rounded-none bg-transparent px-0 text-[15px] focus-visible:ring-0 focus-visible:border-[#0a1030] shadow-none h-9"
                />
              </FieldCard>

              {/* Email */}
              <FieldCard>
                <Label htmlFor="email" className="text-[15px] md:text-[16px] font-bold text-[#1a1a1a]">
                  Best Email To Contact You?
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="you@email.com"
                  className="mt-2 border-0 border-b border-black/10 rounded-none bg-transparent px-0 text-[15px] focus-visible:ring-0 focus-visible:border-[#0a1030] shadow-none h-9"
                />
              </FieldCard>

              {/* Phone */}
              <FieldCard>
                <Label htmlFor="phone" className="text-[15px] md:text-[16px] font-bold text-[#1a1a1a]">
                  Best Number to Text?
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="408-555-0123"
                  className="mt-2 border-0 border-b border-black/10 rounded-none bg-transparent px-0 text-[15px] focus-visible:ring-0 focus-visible:border-[#0a1030] shadow-none h-9"
                />
              </FieldCard>

              {/* Date picker */}
              <FieldCard>
                <Label className="text-[15px] md:text-[16px] font-bold text-[#1a1a1a]">
                  When Should We Expect You?
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'mt-2 w-full flex items-center justify-between text-left border-b border-black/10 pb-2 text-[15px] hover:border-[#0a1030] transition-colors',
                        !form.sunday && 'text-[#8a8478]'
                      )}
                    >
                      <span>
                        {form.sunday
                          ? format(form.sunday, 'EEEE, MMMM d, yyyy')
                          : 'Pick a date'}
                      </span>
                      <CalendarIcon className="h-4 w-4 text-[#1a1a1a]/70" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.sunday}
                      onSelect={(d) => update('sunday', d)}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <p className="mt-2 text-xs text-[#7a7368]">
                  Sunday Worship 10:00 AM · Monday Night Prayer 6:30 PM · Midweek Service Wednesday 7:00 PM
                </p>
              </FieldCard>

              {/* How'd you hear */}
              <FieldCard>
                <div className="text-[15px] md:text-[16px] font-bold text-[#1a1a1a]">
                  How'd You Hear About ACT?
                </div>
                <div className="text-[13px] text-[#7a7368] mt-1">
                  Select from the following options
                </div>
                <RadioGroup
                  value={form.hearAbout}
                  onValueChange={(v) => update('hearAbout', v)}
                  className="mt-5 space-y-3"
                >
                  {hearOptions.map((opt) => (
                    <label
                      key={opt}
                      htmlFor={opt}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <RadioGroupItem
                        id={opt}
                        value={opt}
                        className="h-5 w-5 border-[#0a1030]/40 text-[#0a1030] data-[state=checked]:bg-[#0a1030] data-[state=checked]:border-[#0a1030]"
                      />
                      <span className="text-[15px] text-[#1a1a1a] group-hover:text-black">
                        {opt}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </FieldCard>

              <div className="pt-4 flex justify-center">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white px-12 h-14 text-[15px] font-medium min-w-[240px] disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>

              <p className="text-center text-[12px] text-[#7a7368] pt-2">
                We'll only use your info to prepare for your visit — never shared.
              </p>
            </form>
          )}
        </div>
      </main>

      <Footer />
      <Toaster />
    </div>
  );
};

export default PlanVisit;
